import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { useMyClassesQuery } from '../services/hooks/useEventos';
import ClassRegister from '../components/eventos/ClassRegister';

/** Clave 'YYYY-MM-DD' local, que es lo que espera el backend. */
const dayKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (d: Date, n: number): Date => {
    const copy = new Date(d);
    copy.setDate(copy.getDate() + n);
    return copy;
};

/**
 * P6 fase 2 — la pantalla del instructor: sus clases de un día y la lista de cada
 * una.
 *
 * Es lo ÚNICO que ve un instructor. El resto de la aplicación le responde 403,
 * porque `instructor` solo está en el conjunto `TEACHING`.
 *
 * Un día cada vez a propósito: pasar lista es una tarea de "hoy", y con una clase
 * abierta a la vez no hay forma de marcar a alguien en la clase equivocada.
 */
const MisClasesPage = () => {
    const [dia, setDia] = useState<Date>(new Date());
    const [abierta, setAbierta] = useState<string | null>(null);

    const clave = dayKey(dia);
    const { data, isLoading } = useMyClassesQuery(clave, clave);

    const clases = data?.items ?? [];
    const esHoy = clave === dayKey(new Date());

    const cambiarDia = (n: number) => {
        setDia((d) => addDays(d, n));
        setAbierta(null);
    };

    return (
        <div className="p-4 md:p-8 max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold mb-4">Mis clases</h1>

            <div className="flex items-center justify-between bg-white rounded-lg shadow p-3 mb-4">
                <button
                    onClick={() => cambiarDia(-1)}
                    className="p-2 rounded border hover:bg-gray-50"
                    aria-label="Día anterior"
                >
                    <ChevronLeft size={18} />
                </button>
                <div className="text-center">
                    <div className="font-medium capitalize">
                        {format(dia, "EEEE d 'de' MMMM", { locale: es })}
                    </div>
                    {!esHoy && (
                        <button
                            onClick={() => { setDia(new Date()); setAbierta(null); }}
                            className="text-xs text-brand-primary underline"
                        >
                            Ir a hoy
                        </button>
                    )}
                </div>
                <button
                    onClick={() => cambiarDia(1)}
                    className="p-2 rounded border hover:bg-gray-50"
                    aria-label="Día siguiente"
                >
                    <ChevronRight size={18} />
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center my-8"><Loader2 className="animate-spin" /></div>
            ) : data?.unlinked ? (
                /* Su usuario no está enlazado a ninguna ficha de instructor de este
                   centro. Se dice en voz alta: la alternativa es una pantalla vacía
                   que parece un día libre. */
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                    Tu usuario todavía no está asociado a ninguna ficha de instructor en este
                    centro. Pídele al centro que revise el correo de tu ficha.
                </div>
            ) : clases.length === 0 ? (
                <p className="text-gray-500 text-center my-8">No tienes clases este día.</p>
            ) : (
                <ul className="space-y-3">
                    {clases.map((clase) => {
                        const estaAbierta = abierta === clase._id;
                        return (
                            <li key={clase._id} className="bg-white rounded-lg shadow">
                                <button
                                    onClick={() => setAbierta(estaAbierta ? null : clase._id)}
                                    className="w-full flex items-center justify-between p-4 text-left"
                                >
                                    <div className="min-w-0">
                                        <div className="font-medium">
                                            {clase.horaInicio}–{clase.horaFin}
                                            {clase.nivel?.nombre && ` · ${clase.nivel.nombre}`}
                                        </div>
                                        <div className="text-sm text-gray-500">{clase.sala}</div>
                                    </div>
                                    <span className="flex items-center gap-1 text-sm text-gray-500 shrink-0">
                                        <Users size={14} /> {clase.inscritos}
                                    </span>
                                </button>

                                {estaAbierta && (
                                    <div className="px-4 pb-4">
                                        <ClassRegister eventoId={clase._id} teaching />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default MisClasesPage;
